/**
 * Role-Based Dashboard Panels
 *  - SalesTLDashboardPanel : team performance, approval queue, per-member pipeline
 *  - SalesDashboardPanel   : own KPIs, hot prospect attention, monthly target progress
 *  - RMDashboardPanel      : active accounts focus, renewal calendar, churn risk
 *
 * Rendered by Dashboard.jsx in place of the generic AdminMetricsPanel for
 * non admin/manager roles.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Activity, AlertTriangle, Award, Building, CheckCircle2, Clock, Flame,
  PieChart as PieChartIcon, RefreshCw, Target, TrendingUp, Users, X, Zap,
} from "lucide-react";
import { API } from "../../lib/api";
import { fmtN, fmtDate } from "../../lib/format";
import { useDrilldown } from "./DrilldownContext";

const fmtIDR = (v) =>
  v == null ? "—" :
  v >= 1_000_000_000 ? `Rp ${(v / 1_000_000_000).toFixed(1)} M` :
  v >= 1_000_000     ? `Rp ${(v / 1_000_000).toFixed(1)} Jt` :
  `Rp ${fmtN(v)}`;

function Tile({ icon: Icon, label, value, sub, variant, testid, drill }) {
  const { openDrilldown } = useDrilldown();
  const clickable = !!drill;
  return (
    <div className={`kpi-card ${variant || ""} ${clickable ? "clickable" : ""}`} data-testid={testid}
      onClick={clickable ? () => openDrilldown(drill) : undefined}
      role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => (e.key === "Enter" || e.key === " ") && openDrilldown(drill) : undefined}>
      <div className="kpi-icon"><Icon size={20} /></div>
      <div className="label">{label}</div>
      <div className="value" style={{ fontSize: String(value || "").length > 12 ? 22 : 28 }}>{value}</div>
      <div className="sub">{sub}</div>
      {clickable && <div className="kpi-hint">Klik untuk drill-down →</div>}
    </div>
  );
}

function ProgressBar({ value, target, label }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const color = pct >= 80 ? "var(--good)" : pct >= 50 ? "var(--warn)" : "var(--danger)";
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>
        <span>{label}</span><span><strong style={{ color }}>{value}</strong> / {target}</span>
      </div>
      <div style={{ background: "var(--surface-2)", borderRadius: 6, height: 8, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width .35s" }} />
      </div>
    </div>
  );
}

// Approve & Promote modal — Sales TL one-click promote to New Account Sign
function ApprovePromoteModal({ lead, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const nextYear = new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [sign, setSign] = useState(today);
  const [end, setEnd] = useState(nextYear);
  const [assignedRm, setAssignedRm] = useState("");
  const [rmList, setRmList] = useState([]);
  const [remark, setRemark] = useState(`Approved by Sales TL · ${new Date().toLocaleDateString("id-ID")}`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/leads/meta/rms`).then((r) => setRmList(r.data?.rms || [])).catch(() => {});
  }, []);
  if (!lead) return null;
  const submit = async () => {
    if (!assignedRm) { setErr("Wajib pilih RM yang ditugaskan"); return; }
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/leads/${lead.id}/status`, {
        status: "New Account Sign", remark, tanggal_signing: sign, tanggal_berakhir_kerjasama: end,
        assigned_rm: assignedRm,
      });
      onSuccess?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal promote lead");
    } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" data-testid="approve-promote-modal" style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: "min(560px, 100%)", background: "var(--surface)" }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Zap size={16} color="var(--accent)" />
          <strong>Approve & Promote → New Account Sign</strong>
          <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 8px" }}
            onClick={onClose} data-testid="apm-close"><X size={14} /></button>
        </div>
        <div className="card-b">
          <div style={{ padding: 10, background: "var(--surface-2)", borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
            <strong>{lead.nama_account}</strong><br />
            <span style={{ color: "var(--muted)", fontSize: 11 }}>
              Sales: @{lead.assigned_to} · {fmtN(lead.jumlah_student)} siswa
            </span>
          </div>
          <div style={{ padding: 12, background: "rgba(243,156,18,.08)",
                          border: "1px solid #F5C772", borderRadius: 8, marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#8B5A0F", display: "block", marginBottom: 6 }}>
              🎯 RM yang ditugaskan * (wajib) — akan masuk KPI RM tersebut
            </label>
            <select className="select-input" value={assignedRm}
              onChange={(e) => setAssignedRm(e.target.value)} data-testid="apm-assigned-rm">
              <option value="">— Pilih Relationship Manager —</option>
              {rmList.map((rm) => (
                <option key={rm.username} value={rm.username}>
                  {rm.name} (@{rm.username}){rm.team ? ` · Team ${rm.team}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="field"><label>Tanggal Signing</label>
            <input type="date" className="select-input" value={sign}
              onChange={(e) => setSign(e.target.value)} data-testid="apm-sign-date" />
          </div>
          <div className="field" style={{ marginTop: 10 }}><label>Tanggal Berakhir Kerjasama</label>
            <input type="date" className="select-input" value={end}
              onChange={(e) => setEnd(e.target.value)} data-testid="apm-end-date" />
          </div>
          <div className="field" style={{ marginTop: 10 }}><label>Remark</label>
            <textarea className="select-input" rows={2} value={remark}
              onChange={(e) => setRemark(e.target.value)} data-testid="apm-remark" />
          </div>
          {err && <div className="alert err" style={{ marginTop: 10 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Batal</button>
            <button className="btn btn-primary" onClick={submit} disabled={busy || !assignedRm} data-testid="apm-confirm">
              {busy ? <RefreshCw size={14} className="spin" /> : <CheckCircle2 size={14} />}
              {busy ? " Memproses..." : " Approve & Sign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Quick-Log activity mini form — Sales panel hot prospect attention
function QuickLogModal({ lead, onClose, onSuccess }) {
  const [activityType, setActivityType] = useState("Follow-up Call");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!lead) return null;
  const submit = async () => {
    if (!remark.trim()) { setErr("Remark wajib diisi"); return; }
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/leads/${lead.id}/activities`, {
        activity_type: activityType, remark: remark.trim(),
      });
      onSuccess?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal log activity");
    } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" data-testid="quick-log-modal" style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: "min(480px, 100%)", background: "var(--surface)" }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Zap size={16} color="var(--accent)" /><strong>Quick-Log Activity</strong>
          <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 8px" }}
            onClick={onClose} data-testid="ql-close"><X size={14} /></button>
        </div>
        <div className="card-b">
          <div style={{ padding: 10, background: "var(--surface-2)", borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
            <strong>{lead.nama_account}</strong>
          </div>
          <div className="field"><label>Jenis Aktivitas</label>
            <select className="select-input" value={activityType}
              onChange={(e) => setActivityType(e.target.value)} data-testid="ql-type">
              {["Cold Call","Follow-up Call","Send Proposal","Walk-in",
                "Follow-up Meetings","1st presentation","Qualified Meetings","Other"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginTop: 10 }}><label>Remark / Catatan</label>
            <textarea className="select-input" rows={3} value={remark} placeholder="Contoh: Sudah ditelpon, akan kirim proposal Senin..."
              onChange={(e) => setRemark(e.target.value)} data-testid="ql-remark" />
          </div>
          {err && <div className="alert err" style={{ marginTop: 10 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Batal</button>
            <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="ql-confirm">
              {busy ? <RefreshCw size={14} className="spin" /> : <Zap size={14} />}
              {busy ? " Logging..." : " Log Activity"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────── Sales TL ───────────
export function SalesTLDashboardPanel() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const { openDrilldown } = useDrilldown();
  const load = () => {
    setLoading(true);
    axios.get(`${API}/role-dashboard/sales-tl`)
      .then((r) => setM(r.data))
      .catch(() => setM(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  if (loading) return <div className="empty">Memuat ringkasan tim…</div>;
  if (!m) return null;
  return (
    <div data-testid="sales-tl-panel">
      {toast && <div className="alert ok" style={{ margin: "0 0 12px 0" }} data-testid="stl-toast">{toast}</div>}
      <ApprovePromoteModal lead={promoteTarget} onClose={() => setPromoteTarget(null)}
        onSuccess={() => {
          setToast(`✅ ${promoteTarget.nama_account} berhasil di-promote ke New Account Sign`);
          setPromoteTarget(null);
          load();
          setTimeout(() => setToast(null), 5000);
        }} />
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Users size={14} color="var(--accent)" /> Team Performance · Sales TL View
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }}
          onClick={load} data-testid="stl-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>
      <div className="grid kpi-grid" style={{ marginBottom: 14 }}>
        <Tile icon={Users} label="ANGGOTA TIM" value={fmtN(m.team_size)} sub="Sales + RM aktif" testid="stl-team-size" />
        <Tile icon={Activity} label="TOTAL LEADS" value={fmtN(m.total_leads)} sub="Cakupan seluruh tim" testid="stl-total-leads"
          drill={{ title: "Total Leads (Team)", metric: "leads_total" }} />
        <Tile icon={Flame} label="HOT PROSPECT" value={fmtN(m.total_hot)} sub="Siap dieskalasi" variant="teachcast" testid="stl-hot"
          drill={{ title: "Hot Prospects (Team)", metric: "leads_hot_prospect" }} />
        <Tile icon={Award} label="NEW SIGNS" value={fmtN(m.total_sign)} sub="Total dalam pipeline" variant="sekolah" testid="stl-signs"
          drill={{ title: "New Account Signs (Team)", metric: "leads_new_account_sign" }} />
        <Tile icon={TrendingUp} label="EST. PIPELINE VALUE" value={fmtIDR(m.team_value)} sub="Total estimasi nilai" testid="stl-value"
          drill={{ title: "All Team Pipeline (Est. Value)", metric: "leads_total" }} />
        <Tile icon={AlertTriangle} label="LEADS STALE" value={fmtN(m.stale_leads)} sub=">15 hari tanpa aktivitas" variant="eft" testid="stl-stale"
          drill={{ title: "Stale Leads (>15 hari)", metric: "leads_stale" }} />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1.4fr 1fr", gap: 14, marginTop: 8 }}>
        <div className="card card-3d">
          <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong>Performa per Anggota Tim</strong>
            <span className="badge" style={{ marginLeft: "auto" }}>Sorted by Sign · Hot</span>
          </div>
          <div className="card-b" style={{ padding: 0, overflowX: "auto" }}>
            <table className="data-table" data-testid="stl-member-table" style={{ width: "100%", minWidth: 780 }}>
              <thead>
                <tr>
                  <th>Anggota</th>
                  <th>Team</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th style={{ textAlign: "right" }}>Hot</th>
                  <th style={{ textAlign: "right" }}>Prospect</th>
                  <th style={{ textAlign: "right" }}>Sign</th>
                  <th style={{ textAlign: "right" }} title="Qualified Meetings + Agreement Drafting/Sign + Foundation Sign">Meeting</th>
                  <th style={{ textAlign: "right" }}>Conv %</th>
                  <th style={{ textAlign: "right" }}>Siswa</th>
                  <th style={{ textAlign: "right" }}>Est. Value</th>
                </tr>
              </thead>
              <tbody>
                {m.per_member.map((u) => {
                  const conv = u.total_leads > 0 ? Math.round((u.new_account_sign / u.total_leads) * 100) : 0;
                  return (
                    <tr key={u.username} className="clickable-row"
                      onClick={() => openDrilldown({ title: `Leads · @${u.username}`, metric: "leads_assigned_to", username: u.username })}
                      style={{ cursor: "pointer" }}>
                      <td>
                        <strong>{u.name || u.username}</strong>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>@{u.username} · {u.role}</div>
                      </td>
                      <td>{u.team ? <span className="badge" style={{ background: u.team === "DIAMOND" ? "#c40303" : "#041f60", color: "#fff", fontSize: 10 }}>{u.team}</span> : "—"}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtN(u.total_leads)}</td>
                      <td style={{ textAlign: "right", color: "var(--warn)", fontWeight: 600 }}>{u.hot_prospect}</td>
                      <td style={{ textAlign: "right" }}>{u.prospective}</td>
                      <td style={{ textAlign: "right", color: "var(--good)", fontWeight: 700 }}>{u.new_account_sign}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600,
                                    color: (u.meeting_count || 0) > 0 ? "var(--accent)" : "var(--muted)" }}
                        data-testid={`stl-meeting-${u.username}`}>
                        {u.meeting_count || 0}
                      </td>
                      <td style={{ textAlign: "right", fontSize: 12 }}>
                        <span style={{ background: conv >= 25 ? "rgba(39,174,96,.15)" : conv >= 10 ? "rgba(243,156,18,.15)" : "rgba(231,76,60,.12)",
                                        color: conv >= 25 ? "var(--good)" : conv >= 10 ? "var(--warn)" : "var(--bad)",
                                        padding: "2px 8px", borderRadius: 6, fontWeight: 700 }}>{conv}%</span>
                      </td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtN(u.total_students || 0)}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmtIDR(u.estimated_value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card card-3d">
          <div className="card-h"><strong>Approval Queue</strong>
            <span className="badge" style={{ marginLeft: "auto" }}>Hot Prospect siap di-sign</span>
          </div>
          <div className="card-b" style={{ padding: 0, maxHeight: 480, overflow: "auto" }}>
            {m.approval_queue.length === 0 ? (
              <div className="empty" style={{ padding: 30 }}>Tidak ada lead menunggu approval.</div>
            ) : (
              <table className="data-table" data-testid="stl-approval-queue" style={{ width: "100%" }}>
                <thead><tr>
                  <th>Account</th><th>Sales</th>
                  <th style={{ textAlign: "right" }}>Siswa</th>
                  <th style={{ textAlign: "right" }}>Est.</th>
                  <th>Aksi</th>
                </tr></thead>
                <tbody>
                  {m.approval_queue.map((q) => (
                    <tr key={q.id}>
                      <td><strong>{q.nama_account}</strong>
                        <div style={{ fontSize: 10, color: "var(--muted)" }}>
                          NPSN: {q.npsn || "—"} · {fmtDate(q.updated_at)}
                        </div>
                      </td>
                      <td style={{ fontSize: 11 }}>@{q.assigned_to}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtN(q.jumlah_student)}</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtIDR(q.estimated_value)}</td>
                      <td>
                        <button className="btn btn-primary" style={{ padding: "4px 8px", fontSize: 11 }}
                          onClick={() => setPromoteTarget(q)} data-testid={`apm-btn-${q.id}`}>
                          <Zap size={11} /> Approve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="card card-3d" style={{ marginTop: 16 }}>
        <div className="card-h"><strong>Team Pipeline (per Status)</strong>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted)" }}>Klik tile untuk drill-down</span>
        </div>
        <div className="card-b" style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 10 }}>
          {m.team_pipeline.map((p) => (
            <button key={p.status} className="clickable" data-testid={`stl-pipeline-${p.status.replace(/\s+/g,"-")}`}
              onClick={() => openDrilldown({ title: `Team · ${p.status}`, metric: "leads_by_status", value: p.status })}
              style={{ textAlign: "center", padding: "10px 6px", background: "var(--surface-2)", borderRadius: 8,
                      border: "1px solid transparent", cursor: "pointer", transition: "all .2s" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--accent)" }}>{p.count}</div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{p.status}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────── Sales (individu) ───────────
export function SalesDashboardPanel() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickLog, setQuickLog] = useState(null);
  const [toast, setToast] = useState(null);
  const load = () => {
    setLoading(true);
    axios.get(`${API}/role-dashboard/sales`)
      .then((r) => setM(r.data))
      .catch(() => setM(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  if (loading) return <div className="empty">Memuat dashboard Anda…</div>;
  if (!m) return null;
  return (
    <div data-testid="sales-panel">
      {toast && <div className="alert ok" style={{ margin: "0 0 12px 0" }} data-testid="sp-toast">{toast}</div>}
      <QuickLogModal lead={quickLog} onClose={() => setQuickLog(null)}
        onSuccess={() => {
          setToast(`✅ Activity logged untuk ${quickLog.nama_account}`);
          setQuickLog(null);
          load();
          setTimeout(() => setToast(null), 4000);
        }} />
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Target size={14} color="var(--accent)" /> My Performance · Sales View
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }}
          onClick={load} data-testid="sp-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>
      <div className="grid kpi-grid" style={{ marginBottom: 14 }}>
        <Tile icon={Activity} label="MY LEADS" value={fmtN(m.total_leads)} sub="Yang ditugaskan ke saya" testid="sp-leads"
          drill={{ title: "All My Leads", metric: "leads_total" }} />
        <Tile icon={Flame} label="HOT PROSPECT" value={fmtN(m.hot_prospect)} sub="Siap eskalasi → sign" variant="teachcast" testid="sp-hot"
          drill={{ title: "My Hot Prospects", metric: "leads_hot_prospect" }} />
        <Tile icon={CheckCircle2} label="NEW ACCOUNT SIGN" value={fmtN(m.new_account_sign)} sub="Saya berhasil close" variant="sekolah" testid="sp-signs"
          drill={{ title: "My New Account Sign", metric: "leads_new_account_sign" }} />
        <Tile icon={TrendingUp} label="CONVERSION RATE" value={`${m.conversion_rate}%`} sub="Sign + existing / total" testid="sp-conv"
          drill={{ title: "Saya · Signed + Existing (Conversion Pool)", metric: "leads_won" }} />
        <Tile icon={Building} label="EST. PIPELINE VALUE" value={fmtIDR(m.estimated_value)} sub={`${fmtN(m.total_students)} siswa total`} testid="sp-value"
          drill={{ title: "All My Pipeline (Value)", metric: "leads_total" }} />
        <Tile icon={AlertTriangle} label="STALE LEADS" value={fmtN(m.stale_leads)} sub=">15 hari tidak ada aktivitas" variant="eft" testid="sp-stale"
          drill={{ title: "Stale Leads (Saya · >15 hari)", metric: "leads_stale" }} />
      </div>

      <div className="grid row-split">
        <div className="card">
          <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Target size={14} /><strong>Monthly Target</strong>
            <span className="badge" style={{ marginLeft: "auto" }}>This Month</span>
          </div>
          <div className="card-b">
            <ProgressBar value={m.monthly_target.achieved} target={m.monthly_target.target} label="New Account Sign" />
            <div style={{ marginTop: 14, padding: 10, background: "var(--surface-2)", borderRadius: 8, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Aktivitas bulan ini</span><strong>{fmtN(m.activities_this_month)}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <span>Prospective dalam pipeline</span><strong>{fmtN(m.prospective)}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <span>Cold prospect</span><strong>{fmtN(m.cold_prospect)}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <span>Lost lead</span><strong style={{ color: "var(--danger)" }}>{fmtN(m.lost_lead)}</strong>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Flame size={14} color="var(--accent)" /><strong>Hot Prospect Butuh Perhatian</strong>
            <span className="badge" style={{ marginLeft: "auto" }}>Sorted by last activity ASC</span>
          </div>
          <div className="card-b" style={{ padding: 0, maxHeight: 360, overflow: "auto" }}>
            {m.hot_attention.length === 0 ? (
              <div className="empty">Tidak ada Hot Prospect. Cari peluang baru!</div>
            ) : (
              <table className="data-table" data-testid="sp-hot-attention">
                <thead><tr><th>Account</th><th>Last Activity</th><th>Siswa</th><th>Est.</th><th>Aksi</th></tr></thead>
                <tbody>
                  {m.hot_attention.map((h) => (
                    <tr key={h.id}>
                      <td><strong>{h.nama_account}</strong></td>
                      <td style={{ fontSize: 12 }}>{fmtDate(h.last_activity_at)}</td>
                      <td>{fmtN(h.jumlah_student)}</td>
                      <td>{fmtIDR(h.estimated_value)}</td>
                      <td>
                        <button className="btn btn-primary" style={{ padding: "4px 8px", fontSize: 11 }}
                          onClick={() => setQuickLog(h)} data-testid={`ql-btn-${h.id}`}>
                          <Zap size={11} /> Log
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────── RM ───────────
export function RMDashboardPanel() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickLog, setQuickLog] = useState(null);
  const [toast, setToast] = useState(null);
  const { openDrilldown } = useDrilldown();
  const load = () => {
    setLoading(true);
    axios.get(`${API}/role-dashboard/rm`)
      .then((r) => setM(r.data))
      .catch(() => setM(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  if (loading) return <div className="empty">Memuat data active accounts…</div>;
  if (!m) return null;
  return (
    <div data-testid="rm-panel">
      {toast && <div className="alert ok" style={{ margin: "0 0 12px 0" }} data-testid="rm-toast">{toast}</div>}
      <QuickLogModal lead={quickLog} onClose={() => setQuickLog(null)}
        onSuccess={() => {
          setToast(`✅ Re-engagement logged untuk ${quickLog.nama_account}`);
          setQuickLog(null);
          load();
          setTimeout(() => setToast(null), 4000);
        }} />
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Building size={14} color="var(--accent)" /> Active Accounts · RM View
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }}
          onClick={load} data-testid="rm-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>
      <div className="grid kpi-grid" style={{ marginBottom: 14 }}>
        <Tile icon={CheckCircle2} label="ACTIVE ACCOUNTS" value={fmtN(m.active_accounts)} sub="New Sign + Existing" variant="sekolah" testid="rm-active"
          drill={{ title: "Active Accounts", metric: "leads_active_accounts" }} />
        <Tile icon={Award} label="PORTFOLIO STUDENTS" value={fmtN(m.portfolio_students)} sub="Total siswa terlayani" variant="teachcast" testid="rm-students"
          drill={{ title: "Active Accounts (Portfolio Students)", metric: "leads_active_accounts" }} />
        <Tile icon={TrendingUp} label="PORTFOLIO VALUE" value={fmtIDR(m.portfolio_value)} sub="Total estimasi nilai" testid="rm-value"
          drill={{ title: "Active Accounts (Portfolio Value)", metric: "leads_active_accounts" }} />
        <Tile icon={Clock} label="RENEWAL DALAM 90 HARI" value={fmtN(m.renewals_90d.length)} sub="Kerjasama segera berakhir" testid="rm-renewal-count"
          drill={{ title: "Renewal dalam 90 Hari", metric: "leads_renewal_90d" }} />
        <Tile icon={AlertTriangle} label="CHURN RISK" value={fmtN(m.churn_risk.length)} sub=">60 hari tanpa aktivitas" variant="eft" testid="rm-risk"
          drill={{ title: "Churn Risk Accounts", metric: "leads_churn_risk" }} />
        <Tile icon={AlertTriangle} label="CHURN ACCOUNTS" value={fmtN(m.churn_accounts)} sub="Sudah churn" variant="eft" testid="rm-churn"
          drill={{ title: "Churn Accounts (sudah churn)", metric: "leads_churn_accounts" }} />
      </div>

      <div className="grid row-split">
        <div className="card">
          <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Clock size={14} color="var(--warn)" /><strong>Renewal Calendar (90 hari ke depan)</strong>
          </div>
          <div className="card-b" style={{ padding: 0, maxHeight: 320, overflow: "auto" }}>
            {m.renewals_90d.length === 0 ? (
              <div className="empty">Belum ada renewal dalam 90 hari ke depan.</div>
            ) : (
              <table className="data-table" data-testid="rm-renewals">
                <thead><tr><th>Account</th><th>Berakhir</th><th>Sisa Hari</th><th>Paket</th><th>Est.</th></tr></thead>
                <tbody>
                  {m.renewals_90d.map((r) => {
                    const urgent = r.days_left <= 30;
                    return (
                      <tr key={r.id}>
                        <td><strong>{r.nama_account}</strong></td>
                        <td>{r.tanggal_berakhir}</td>
                        <td><span style={{ color: urgent ? "var(--danger)" : "var(--warn)", fontWeight: 600 }}>
                          {r.days_left}d {urgent ? "🔥" : ""}
                        </span></td>
                        <td>{r.course_package || "—"}</td>
                        <td>{fmtIDR(r.estimated_value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} color="var(--danger)" /><strong>Churn Risk · Butuh Re-engagement</strong>
          </div>
          <div className="card-b" style={{ padding: 0, maxHeight: 320, overflow: "auto" }}>
            {m.churn_risk.length === 0 ? (
              <div className="empty">Semua account engaged dalam 60 hari terakhir. 👏</div>
            ) : (
              <table className="data-table" data-testid="rm-churn-risk">
                <thead><tr><th>Account</th><th>Inactive</th><th>Siswa</th><th>Est.</th><th>Aksi</th></tr></thead>
                <tbody>
                  {m.churn_risk.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.nama_account}</strong><br />
                        <span style={{ fontSize: 11, color: "var(--muted)" }}>Last: {fmtDate(c.last_activity_at)}</span>
                      </td>
                      <td><span style={{ color: "var(--danger)", fontWeight: 600 }}>{c.days_inactive}d</span></td>
                      <td>{fmtN(c.jumlah_student)}</td>
                      <td>{fmtIDR(c.estimated_value)}</td>
                      <td>
                        <button className="btn btn-primary" style={{ padding: "4px 8px", fontSize: 11 }}
                          onClick={() => setQuickLog(c)} data-testid={`rm-ql-btn-${c.id}`}>
                          <Zap size={11} /> Re-engage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {m.package_distribution.length > 0 && (
        <div className="card card-3d" style={{ marginTop: 16 }}>
          <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PieChartIcon size={14} color="var(--accent)" /><strong>Distribusi Paket di Active Accounts</strong>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted)" }}>Klik untuk drill-down</span>
          </div>
          <div className="card-b" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            {m.package_distribution.map((p) => (
              <button key={p.package} className="clickable"
                onClick={() => openDrilldown({ title: `Active Accounts · ${p.package}`, metric: "leads_by_package_signed", value: p.package })}
                data-testid={`rm-pkg-${(p.package || "").replace(/\s+/g, "-")}`}
                style={{ textAlign: "center", padding: 12, background: "var(--surface-2)",
                          borderRadius: 8, border: "1px solid transparent", cursor: "pointer" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--accent)" }}>{p.count}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{p.package}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
